import cubejs from '@cubejs-client/core';
import { useEffect, useRef, useState } from 'react';
import { useGetCubeToken } from "../apiCalls/initalize-cube";
import { useCubeDataDispatch, useCubeData } from '../contexts/CubeDataContext';
import configureFilterValueOptions from './filters/configureFilterValueOptions';

// This component is a derivative of AppDataLoader that specifically redos the Cube data fetching now utilizing account data and can be called at anytime within the app.

export function useUpdateCubeDataHook() {
    const { createCubeTokenOnLoad } = useGetCubeToken();
    const cubeDataDispatch = useCubeDataDispatch();
    const { cubeMetaData } = useCubeData();
    const controller = new AbortController();
    const { signal } = controller;

    // Store latest arguments if cubeMetaData is empty
    const lastArgsRef = useRef(null);
    const [shouldRetry, setShouldRetry] = useState(false);

    const updateCubeData = async ({ selectedAccount, useDemoData, dataTableList, customJoinList }) => {
        try {
            cubeDataDispatch({
                type: "setInitializingData",
                payload: true
            });

            // If cubeMetaData is not ready, store args and exit early
            if (Object.keys(cubeMetaData).length === 0) {
                lastArgsRef.current = { selectedAccount, useDemoData, dataTableList };
                setShouldRetry(true);
                return { status: "Waiting for cubeMetaData" };
            }

            if (dataTableList.length === 0 ||
                dataTableList.every(dataTable => dataTable.fetched) ||
                dataTableList.every(dataTable => !dataTable.anchorDataTable)) {
                dataTableList.forEach(dataTable => {
                    if(dataTable?.anchorDataTable){
                        cubeDataDispatch({
                            type: 'setCurrentCubeAnchorSlug',
                            payload: { anchorTableSlug: dataTable?.slug, useDemoData }
                        });
                    }
                })
                cubeDataDispatch({
                    type: "setInitializingData",
                    payload: false
                });
                return { status: "Everything is in context already" };
            }

            const { cubeToken, dataSetObject, customMetaData ={} } = await createCubeTokenOnLoad({ selectedAccount, signal, dataTableList, useDemoData, customJoinList });

            const allMetaData = { ...cubeMetaData, ...customMetaData };
            if (!cubeToken) {
                console.error('Cube token not found');
                return { status: null };
            }

            const dataTokenObject = cubejs(cubeToken, {
                apiUrl: `${process.env.REACT_APP_CUBE_URL}`,
                headers: {
                    Authorization: `Bearer ${cubeToken}`
                },
                method: 'POST'
            });

            if (!dataTokenObject) {
                return { status: null };
            }

            const dataLoaders = Object.keys(dataSetObject).map(anchorCubeName =>
                anchorTableCubeDataLoader({
                    key: anchorCubeName,
                    anchorTableSlug: anchorCubeName,
                    joinTables: dataSetObject[anchorCubeName],
                    allMetaData: allMetaData,
                    apiConfig: dataTokenObject,
                    cubeDataDispatch,
                    onlyAnchorFetched: dataTableList?.length === 1,
                    useDemoData
                })
            );

            await Promise.all(dataLoaders);

            cubeDataDispatch({
                type: "setInitializingData",
                payload: false
            });

            return { status: true };
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error("Error in updateCubeData:", error);
            }
            return { status: false };
        }
    };

    // Retry when cubeMetaData becomes available
    useEffect(() => {
        if (shouldRetry && Object.keys(cubeMetaData).length > 0 && lastArgsRef.current) {
            updateCubeData(lastArgsRef.current);
            lastArgsRef.current = null; // Clear stored args after retry
            setShouldRetry(false); // Reset retry flag
        }
    }, [cubeMetaData]);

    return { updateCubeData, controller };
}

async function anchorTableCubeDataLoader({ anchorTableSlug, joinTables, allMetaData, apiConfig, cubeDataDispatch, onlyAnchorFetched, useDemoData }) {

    function onAnchorMetaComplete(query, columnMetaData, filterColumnOptions) { //first function to run, sets the anchor table meta data
        cubeDataDispatch({
            type: 'setAnchorMetaData',
            payload: {
                tableSlug: anchorTableSlug,
                metaData: { query, columnMetaData, filterColumnOptions },
                onlyAnchorFetched,
                useDemoData
            }
        });
        return query
    }

    function onJoinTableMetaComplete(tableSlug, query, columnMetaData, filterColumnOptions) { //third function to run, sets the join table meta data

        cubeDataDispatch({
            type: 'setJoinTableMetaData',
            payload: {
                anchorTableSlug,
                tableSlug,
                metaData: { query, columnMetaData, filterColumnOptions },
                useDemoData
            }
        });
    }

    function onAnchorFinished(tableData, metaData) { //second function to run, sets the anchor table data

        cubeDataDispatch({
            type: 'setCurrentCubeAnchorSlug',
            payload: { anchorTableSlug, useDemoData }
        });

        configureFilterValueOptions({ tableData, anchorTableSlug, metaData }).then(filterValueOptions => {
            cubeDataDispatch({
                type: "addFilterValueOptions",
                payload: {
                    anchorTableSlug,
                    filterValueOptions,
                    isAnchorTable: true,
                    useDemoData
                }
            })
        });
        cubeDataDispatch({
            type: "setAnchorTableData",
            payload: { tableSlug: anchorTableSlug, tableData, onlyAnchorFetched, useDemoData }
        });
    }

    function onJoinFinished(tableSlug, tableData, metaData) { //fourth function to run, sets the join table data

        configureFilterValueOptions({ tableData, tableSlug, metaData }).then(filterValueOptions => {
            cubeDataDispatch({
                type: "addFilterValueOptions",
                payload: {
                    anchorTableSlug,
                    filterValueOptions,
                    tableSlug,
                    useDemoData
                }
            })
        });
        cubeDataDispatch({
            type: "setJoinTableData",
            payload: {
                anchorTableSlug,
                tableSlug,
                tableData,
                useDemoData
            }
        });
    }

    await CubeDataLoader({
        metaData: allMetaData[anchorTableSlug],
        onMetaComplete: onAnchorMetaComplete,
        onComplete: (data, metaData) => onAnchorFinished(data, metaData),
        apiConfig
    }).then((anchorQuery) =>
        Object.keys(joinTables).forEach(joinSlug => {
            CubeDataLoader({
                metaData: allMetaData[joinSlug],
                anchorQuery,
                onMetaComplete: (query, columnMetaData, filterColumnOptions) => onJoinTableMetaComplete(joinSlug, query, columnMetaData, filterColumnOptions),
                onComplete: (data, metaData) => onJoinFinished(joinSlug, data, metaData),
                apiConfig
            });
        }))
}

async function CubeDataLoader({ metaData, onMetaComplete, onComplete, anchorQuery, apiConfig }) {
    const query = {
        limit: 50000,
        dimensions: anchorQuery ? [...anchorQuery.dimensions] : [],
        measures: anchorQuery ? [...anchorQuery.measures] : [],
    };

    const gridColumnMetaData = [];
    const filterColumnOptions = {};

    // Configure the query and gridColumnMetaData
    metaData?.dimensions?.forEach(dimension => {
        if (dimension.isVisible !== false) {
            query.dimensions.push(dimension?.name);
            gridColumnMetaData.push(dimension);
            if (dimension?.name) {
                filterColumnOptions[dimension.name] = dimension;
            }
        }
    });

    // Pass the completed query to the callback function
    if (onMetaComplete) {
        onMetaComplete(query, gridColumnMetaData, filterColumnOptions);
    }

    // Trigger the query runner if the query is valid
    if (query.dimensions.length > 0 || query.measures.length > 0) {
        await CubeQueryRunner({
            query,
            callback: onComplete,
            apiConfig,
            metaData: gridColumnMetaData
        });
    }

    return query
}

async function CubeQueryRunner({ query, callback, apiConfig, metaData }) {
    const resultSet = await apiConfig.load(query, { 
        castNumerics: true,
        method: 'POST' // using POST to avoid URL length limits for large queries
    })
    if (resultSet) {
        callback(resultSet.tablePivot(), metaData);
    }
}